import type { CommerceAPI, ShopperBaskets, ShopperCustomers } from 'commerce-api'
import AlgoliaAnalytics, { InsightsMethodMap } from 'search-insights'
import {
  action,
  computed,
  flow,
  flowResult,
  makeObservable,
  observable,
  reaction,
} from 'mobx'
import { format } from 'date-fns'
import { isMatchingAddress, getDeviceType, isServer } from '../utils/utils'
import { DateUtils } from '../utils/date-utils'
import { RootStore } from './RootStore'
import { StoreModel, Wrapper } from './utils'
import { CustomerCredentials } from './CustomerStore'
import { NotificationType } from '../types/ui'
import { PaymentInstrument, Status, StatusMap } from '../types/commerce'
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { daysOfTheWeek, months } from '../constants'
import {
  AddressFormType,
  ATBEvent,
  BasketMergeTypes,
  BasketPromotionsThreshold,
  BasketPromotionsThresholdResponse,
  BasketResult,
  BeforeYouGoPages,
  CustomerBasket,
  DeliverySlot,
  MissedOffers,
  OrderAddress,
  OrderResponse,
  PartialProductItem,
  PreviouslyRemovedItem,
  ReserveSlotResponse,
  StoreBasket,
  TimeSlot,
  TProductItem,
} from '../types/store/basket'
import { LocalStorage } from '../utils/storages'
import { ALCOHOL_PRODUCT_CLASS, LOCALSTORAGE_KEYS, PAYMENT_METHOD_IDS } from '../utils/constants'
import { StoreDetailResponseItem } from '../types/store/storeDetails'
import { Nullable } from '../types/utils'
import { SubmitOrderData } from '../types/store/basket'
import { CheckoutBonusCardStatus } from '../types/store/common'
import { PaymentSummaries } from '../types/payments'
import { RegionalIndexConfig } from '../types/algolia'
import { sendRMClicks } from '../analytics/retail-media-tracking'
import { Promotion } from './ProductStore'

const MissingBasketError = new Error('Missing basket')

export class BasketStore implements StoreModel {
  api: CommerceAPI
  rootStore: RootStore
  basket?: StoreBasket
  status: Status
  deliverySlotStatus: Status
  deliverySlots: Nullable<DeliverySlot>
  nextAvailableSlot: Nullable<TimeSlot>
  deliverySlotPostalCode: Nullable<string>
  nearestStoreByDeliveryPostCode: Nullable<StoreDetailResponseItem>
  initMergingUI: boolean
  basketMergeData: string
  basketMergeType: Nullable<BasketMergeTypes>
  openMiniBasketUI: boolean
  beforeYouGoPages: Nullable<BeforeYouGoPages>
  missedOffers: Nullable<MissedOffers>
  promotionalThresholds: Array<BasketPromotionsThreshold>
  filteredPromotionalThresholds: Array<BasketPromotionsThreshold>
  bonusCardStatus: CheckoutBonusCardStatus
  isAppliedPostCodeDeliverable: Nullable<boolean>
  processingAppliedPostCode: boolean
  productsToUpdate: Array<PartialProductItem>
  productsToUpdateTimeout: ReturnType<typeof setTimeout>
  pendingProducts: Array<PartialProductItem>
  pendingATBEvent: Nullable<ATBEvent>
  pendingPromo: Promotion | undefined
  postcodeError: boolean
  deliveryAddressFormExpanded : AddressFormType | null

  constructor(
    rootStore: RootStore,
    initialState?: ReturnType<RootStore['getState']>['basketStore']
  ) {
    this.basket = initialState?.basket
    this.status = StatusMap.INIT
    this.deliverySlotStatus = StatusMap.INIT
    this.deliverySlots = null
    this.nextAvailableSlot = null
    this.deliverySlotPostalCode = null
    this.beforeYouGoPages = null
    this.missedOffers = null
    this.initMergingUI = false
    this.openMiniBasketUI = false
    this.basketMergeData = ''
    this.basketMergeType = null
    this.promotionalThresholds = []
    this.filteredPromotionalThresholds = []
    this.nearestStoreByDeliveryPostCode = null
    this.bonusCardStatus = { isServiceActive: true }
    this.isAppliedPostCodeDeliverable = null
    this.processingAppliedPostCode = false
    this.productsToUpdate = []
    this.productsToUpdateTimeout = setTimeout(() => {})
    this.pendingProducts = []
    this.pendingATBEvent = null
    this.pendingPromo = undefined
    this.postcodeError = false
    this.deliveryAddressFormExpanded = null

    makeObservable(this, {
      basket: observable,
      productsToUpdate: observable,
      pendingProducts: observable,
      pendingATBEvent: observable,
      initMergingUI: observable,
      openMiniBasketUI: observable,
      basketMergeData: observable,
      basketMergeType: observable,
      status: observable,
      deliverySlotStatus: observable,
      deliverySlots: observable,
      nextAvailableSlot: observable,
      deliverySlotPostalCode: observable,
      beforeYouGoPages: observable,
      missedOffers: observable,
      promotionalThresholds: observable,
      filteredPromotionalThresholds: observable,
      nearestStoreByDeliveryPostCode: observable,
      bonusCardStatus: observable,
      isAppliedPostCodeDeliverable: observable,
      processingAppliedPostCode: observable,
      deliveryAddressFormExpanded: observable,
      loaded: computed,
      hasItems: computed,
      basketHash: computed,
      hasAlcoholItems: computed,
      restrictedProductClasses: computed,
      previouslyRemovedItems: computed,
      productIds: computed,
      postcodeError: observable,
      applicableShippingMethods: computed,
      count: computed,
      allProductLineItems: computed,
      paymentMethod: computed,
      shippingAddress: computed,
      billingAddress: computed,
      basketSubTotal: computed,
      bonusCardMax: computed,
      isBillingSameAsShipping: computed,
      deliveryPostalCode: computed,
      sameDayDeliveryAvailable: computed,
      currentDeliverySlotId: computed,
      currentDeliveryStoreId: computed,
      deliveryNotes: computed,
      currentDeliverySlot: computed,
      currentDeliveryDay: computed,
      deliverySlotArray: computed,
      excludeSubstitutes: computed,
      isCarrierBagEditable: computed,
      isCarrierBagAdded: computed,
      carrierBagCharge: computed,
      bonusLineItemAmount: computed,
      addressDisplay: computed,
      deliveryDate: computed,
      minimumBasketValue: computed,
      basketCarrierBagCharge: computed,
      basketDeliveryCharge: computed,
      basketFreeDeliveryThreshold: computed,
      basketSmallBasketCharge: computed,
      basketSmallBasketChargeThreshold: computed,
      basketItemsPriceBeforeDiscount: computed,
      appliedPromocode: computed,
      subtotal: computed,
      canAddItems: computed,
      mergeBasketTotal: computed,
      mergeBasketCount: computed,
      removePreviouslyRemovedItem: flow,
      updateBasketCurrency: flow,
      getBasket: flow.bound,
      updateBasketItemQuantity: flow.bound,
      clearPreviouslyRemovedItems: flow.bound,
      getOrCreateBasket: flow.bound,
      checkoutLogin: flow.bound,
      addOrUpdateBillingAddress: flow.bound,
      addOrUpdateShippingAddress: flow.bound,
      getShippingMethods: flow.bound,
      setShippingMethod: flow.bound,
      setCustomerPaymentMethod: flow.bound,
      submitOrder: flow.bound,
      submitPreBookPurchase: flow.bound,
      deleteCustomerPaymentMethod: flow.bound,
      setPromoCode: flow.bound,
      removePromoCode: flow.bound,
      updateBasketSubstitutes: flow.bound,
      updateBasketDeliveryNote: flow.bound,
      updateCarrierBag: flow.bound,
      slotTime: action.bound,
      setCustomerEmail: flow.bound,
      setPostCode: flow.bound,
      clearPostCode: flow.bound,
      getDeliverySlots: flow.bound,
      getNextAvailableSlot: flow.bound,
      reserveDeliverySlot: flow.bound,
      getBeforeYouGoPages: flow.bound,
      mergeBaskets: flow.bound,
      getMissedOffers: flow.bound,
      getBasketPromotionalThreshold: flow.bound,
      transformPromotionalThresholds: flow.bound,
      retrieveParkedCartBack: flow.bound,
      handlePostcodeDeliveryStatus: flow.bound,
      setBonusCardStatus: action.bound,
      setNearestStore: action.bound,
      runATBAnalytics: action.bound,
      setBasketMergeType: action.bound,
      setOpenMiniBasketUI: action.bound,
      setPostcodeError: action.bound,
      buildThresholdItem: action.bound,
      setBasket: action.bound,
      setStatus: action.bound,
      setDeliverySlotStatus: action.bound,
      setIsAppliedPostCodeDeliverableValue: action.bound,
      setMissedOffers: action.bound,
      setProductsToUpdate: action.bound,
      setInitMergingUI: action.bound,
      setBasketMergeData: action.bound,
      addProductToUpdate: action.bound,
      setPendingProducts: action.bound,
      setPendingATBEvent: action.bound,
      setPendingPromoModal: action.bound,
      setDeliverySlots: action.bound,
      setDeliverySlotPostalCode: action.bound,
      setBeforeYouGoPages: action.bound,
      setPromotionalThresholds: action.bound,
      setFilteredPromotionalThresholds: action.bound,
      setProcessingAppliedPostCode: action.bound,
      checkIfProductIsRestricted: action.bound,
      findRestrictedProductLimit: action.bound,
    })
    this.api = rootStore.api
    this.rootStore = rootStore

    reaction(
      () => ({
        length: this.productsToUpdate.length,
        quantity: this.productsToUpdate.map((product) => product.quantity),
      }),
      async () => {
        // Start the timer again if there is another change in quantity or length
        clearTimeout(this.productsToUpdateTimeout)

        if (this.productsToUpdate.length) {
          this.productsToUpdateTimeout = setTimeout(async () => {
            this.updateBasketItemQuantity(this.productsToUpdate)
            this.setProductsToUpdate([])
          }, 1000)
        }
      }
    )

    // When a signed-out user clicks ATB, the products are stored here.
    // Once they log in and a basket is ready, this reaction adds them automatically.
    reaction(
      () => ({
        pendingProducts: this.pendingProducts,
        isRegistered: this.rootStore.customerStore?.isRegistered,
        basketId: this.basket?.basketId,
        pendingPromo: this.pendingPromo
      }),
      async ({ pendingProducts, isRegistered, basketId, pendingPromo }) => {
        if (pendingProducts.length === 0 || !isRegistered || !basketId) return
        const products = pendingProducts
        this.setPendingProducts([])

        for (const product of products) {
          try {
            const existing = this.basket?.productItems?.find((item) => item.productId === product?.product?.id)
            const quantity = (existing?.quantity || 0) + (product?.quantity || 1)
              await flowResult(
                this.updateBasketItemQuantity({
                  product: { id: product?.product?.id},
                  quantity,
                } as PartialProductItem)
              )
            this.setOpenMiniBasketUI(true)
            if (pendingPromo) {
              this.rootStore.globalStore.setIsPromoModalOpen(true, pendingPromo)
              this.setPendingPromoModal(undefined)
            }
        } catch (error) {
            console.error('Failed to add pending product to basket:', error)
          }
        }
      }
    )
  }

  get asJson() {
    return {
      basket: this.basket,
      status: this.status,
      deliverySlotStatus: this.deliverySlotStatus
    }
  }

  get loaded() {
    return this.basket?.basketId != null
  }

  get hasItems() {
    if (!this.basket || !this.basket.productItems) {
      return false
    }
    return this.basket.productItems.length > 0
  }

  get basketHash() {
    return this.basket?.c_naItemsBasketHash || ''
  }

  get hasAlcoholItems() {
    if (!this.basket || !this.basket.productItems) {
      return false
    }

    const { productStore } = this.rootStore

    return this.basket.productItems.some((productItem) => {
      const productId = productItem?.productId
      if (!productId) return false

      const productData = productStore.productsById[productId]

      if (!productData) return false

      if (productData.productClasses?.includes(ALCOHOL_PRODUCT_CLASS)) {
        return true
      }

      // Check bundled products if any present
      if (Array.isArray(productData.bundledProducts) && productData.bundledProducts.length) {
        return productData.bundledProducts.some((bundledProduct) => {
          const bundledProductData = productStore.productsById[bundledProduct.id]
          return bundledProductData?.productClasses?.includes(ALCOHOL_PRODUCT_CLASS) ?? false
        })
      }

      return false
    })
  }

  get productIds() {
    return (this.basket?.productItems ?? []).reduce((acc, item) => {
      if (item.productId) {
        acc.push(item.productId)
      }
      return acc
    }, [] as string[])
  }

  get count() {
    return this.basket?.productQuantityTotal ?? 0
  }

  get allProductLineItems() {
    const productLineItems = this.basket?.productItems ?? []
    const bonusProductLineItems = this.basket?.bonusLineItems ?? []
    return [...productLineItems, ...bonusProductLineItems]
  }

  get groupedBonusItems(): ProductItem[] {
    return Object.values(
      this.basket?.bonusLineItems?.reduce((acc: ProductItem, item: ProductItem) => {
        const productID = item.productID;

        if (!acc[productID]) {
          acc[productID] = { ...item, quantity: 1 };
        } else {
          acc[productID].quantity += 1;
        }

        return acc;
      }, {} as Record<string, ProductItem>)
    );
  }

  get paymentMethod() {
    return this.basket?.paymentInstruments && this.basket.paymentInstruments[0]
      ? this.basket.paymentInstruments[0]
      : null
  }

  get shippingAddress() {
    return this.basket?.shipments && this.basket?.shipments[0]
      ? this.basket?.shipments[0].shippingAddress
      : null
  }

  get applicableShippingMethods() {
    return this.basket?.applicableShippingMethods || null
  }

  get defaultShippingMethodId() {
    return this.basket?.defaultShippingMethodId || ''
  }

  get shippingMethod() {
    return this.basket?.shipments && this.basket?.shipments[0]
      ? this.basket?.shipments[0].shippingMethod
      : null
  }

  get billingAddress() {
    return this.basket?.billingAddress && this.basket?.billingAddress.address1 !== '' ? this.basket.billingAddress : this.shippingAddress || null
  }

  get basketSubTotal() {
    return this.basket ? this.basket?.productSubTotal : 0
  }

  get minimumBasketValue() {
    const minDeliveryThreshold = this.filteredPromotionalThresholds?.find(x => x.thresholdSource === 'minDelivery')
    if (minDeliveryThreshold) {
      return minDeliveryThreshold.thresholdValue
    } else {
      return 0
    }
  }

  get basketDeliveryCharge() {
    if (isNaN(this.basket?.shipments?.[0]?.shippingMethod?.c_deliveryCharge)) {
      return 0
    } else {
      return this.basket?.shipments?.[0]?.shippingMethod?.c_deliveryCharge
    }
  }

  get basketCarrierBagCharge() {
    if (!this.basket?.c_isCarrierBagAdded) {
      return null
    }

    return this.basket?.shipments?.length
      ? this.basket?.shipments?.[0]?.shippingMethod?.c_carrierBagCharge ?? null
      : null
  }

  get basketFreeDeliveryThreshold(): number | null {
    return this.basket?.shipments?.length
      ? this.basket?.shipments?.[0]?.shippingMethod?.c_freeDeliveryThreshold ?? null
      : null
  }

  get basketSmallBasketCharge(): number | null {
    return this.basket?.shippingItems?.length
      ? this.basket?.shippingItems?.[0]?.c_smallBasketCharge ?? null
      : null
  }

  get basketSmallBasketChargeThreshold(): number | null {
    return this.basket?.shippingItems?.length
      ? this.basket?.shippingItems?.[0]?.c_smallBasketChargeThreshold ?? null
      : null
  }

  get basketItemsPriceBeforeDiscount(): number {
    if (!this.basket) return 0

    const productsPriceTotal =
      this.basket?.productItems?.reduce((total, product) => {
        if (!product.price) return total
        total = total + product.price
        return total
      }, 0) || 0

    const { orderPriceAdjustments } = this.basket

    const priceAdjustmentsTotal =
      orderPriceAdjustments?.reduce(
        (total, adjustment) => (total += Math.abs(adjustment.price ?? 0)),
        0
      ) || 0

    return productsPriceTotal + priceAdjustmentsTotal
  }

  get subtotal() {
    if (!this.basket || !this.hasItems) return 0

    return this.basket.productItems?.reduce((subtotal, { price }) => (subtotal += price ?? 0), 0) ?? 0
  }

  get bonusCardMax() {
    const { orderStore, customerStore } = this.rootStore
    const orderAmount = orderStore.editMode ? orderStore.orderEditOrderTotal : orderStore.orderTotal

    const maxBalance = Math.min(
      Number(customerStore?.bonusCardBalance ?? 0),
      Number(orderAmount ?? 0)
    )

    return maxBalance.toFixed(2) ?? '0'
  }

  get isBillingSameAsShipping() {
    if (!this.shippingAddress && !this.billingAddress) {
      return false
    }
    if (!this.shippingAddress) {
      return false
    }
    if (!this.billingAddress && this.shippingAddress) {
      return true
    }
    if (!this.billingAddress) {
      return false
    }
    const result = isMatchingAddress(this.billingAddress, this.shippingAddress)
    return result
  }

  get deliveryPostalCode() {
    return this.basket?.c_deliveryPostalCode
      ? this.basket?.c_deliveryPostalCode // Use the basket deliveryPostalCode if available
      : this.rootStore.customerStore.primaryAddress?.postalCode ?? null // Fallback to customer primary address postal code
  }

  get sameDayDeliveryAvailable() {
    return !!this.deliverySlotArray?.[0]?.hasAvailableSlots
  }

  get currentDeliverySlotId() {
    return this.basket?.c_deliverySlotId
  }

  get currentDeliveryStoreId() {
    return this.basket?.c_deliveryStoreId || this.rootStore.customerStore.deliveryStoreId
  }

  get deliveryNotes() {
    return this.basket?.c_deliveryNotes || ''
  }

  get isCarrierBagEditable() {
    return this.basket?.c_isCarrierBagEditable
  }

  get isCarrierBagAdded() {
    return this.basket?.c_isCarrierBagAdded
  }

  get carrierBagCharge() {
    return this.basket?.shipments?.[0]?.shippingMethod?.c_carrierBagCharge ?? 0
  }

  get bonusLineItemAmount() {
    return this.basket?.bonusLineItems?.length
  }

  get deliverySlotArray(): DeliverySlot[] {
    // Check if deliverySlots is not null
    if (this.deliverySlots) {
      // If deliverySlots is not null, safely use Object.values
      return Object.values(this.deliverySlots)
    } else {
      // If deliverySlots is null, return an empty array
      return []
    }
  }

  get excludeSubstitutes() {
    return this.basket?.c_excludeSubstitutes
  }

  get currentDeliverySlot() {
    const _slots = this.deliverySlotArray
    let currentSlot = { day: {} as DeliverySlot, timeSlot: null as TimeSlot | null }

    _slots.forEach(({ slots }, idx) => {
      const matchingSlot = slots?.find(({ id }) => id === this.currentDeliverySlotId)
      if (matchingSlot) {
        currentSlot = {
          timeSlot: matchingSlot,
          day: _slots[idx],
        }
      }
    })
    return currentSlot
  }

  get currentDeliveryDay() {
    const { orderStore } = this.rootStore
    const { timeSlot } = this.currentDeliverySlot
    const startTime = new Date(this.basket?.c_windowStartTime)
    let dayText = daysOfTheWeek[startTime?.getDay()]
    const isToday = new Date().getDay() === startTime.getDay()
    const isTomorrow = new Date().setDate(new Date().getDate() + 1) === startTime.getDate()
    const currentDate = new Date().getTime()
    const expiryTime = new Date(this.basket?.c_slotExpiryTime).getTime()
    const slotTimeLeft = expiryTime - currentDate

    if (isToday) {
      dayText = 'Today'
    } else if (isTomorrow) {
      dayText = 'Tomorrow'
    }

    // Checking if we are in EDIT MODE, and we haven't changed delivery, initial delivery is reserved.
    let isAvailable = (orderStore.editMode && !orderStore.orderEditDeliverySlotChanged) || false

    if (timeSlot?.available || slotTimeLeft > 0) {
      isAvailable = true
    }

    return {
      dayText,
      day: startTime?.getDate()?.toString(),
      month: months[startTime?.getMonth()],
      startHour: timeSlot?.startHour,
      endHour: timeSlot?.endHour,
      isAvailable,
    }
  }

  get deliveryDate() {
    const startTime = this.basket?.c_windowStartTime
    let date = ''
    if (startTime) {
      date = new Date(startTime)
        .toLocaleDateString('CA')
        .split('/')
        .map((datePart: string) => (datePart?.length === 1 ? `0${datePart}` : datePart))
        .join('/')
    }
    return date
  }

  get addressDisplay() {
    const { address1, address2, city, postalCode } = this.shippingAddress || {}
    return [address1, address2, city, postalCode].filter(Boolean).join(', ')
  }

  get appliedPromocode() {
    return this.basket?.couponItems?.find(x => x.statusCode === "APPLIED") ?? null
  }

  get shippingPromotion() {
    return this.missedOffers?.shippingPromos?.length ? this.missedOffers?.shippingPromos[0] : null
  }

  get calculatedDeliveryCost() {
    const deliveryCost = this.basket?.shippingItems?.[0]?.c_deliveryCharge ?? 0
    if (this.shippingPromotion?.applied) {
      return deliveryCost + (this.shippingPromotion.discount ?? 0)
    } else {
      return deliveryCost
    }
  }

  get orderDiscount() {
    const orderDiscount = this.basket?.orderDiscount ?? 0
    if (this.shippingPromotion) {
      return orderDiscount + (this.shippingPromotion.discount ?? 0)
    } else {
      return orderDiscount
    }
  }

  get canAddItems() {
    return (
      this.basket?.c_deliveryPostalCode && (
        this.basket?.c_deliveryStoreId ||
        this.rootStore.customerStore.deliveryStoreId)
    )
  }

  get mergeBasketTotal() {
    let total
    if (this.basketMergeType !== 'edit') {
      if (this.basket?.c_storedBasketJSON?.length) {
        try {
          const storedBasket = JSON.parse(this.basket?.c_storedBasketJSON)
          total = storedBasket?.basketTotal?.value
        } catch (e) {
          total = 0
        }
      }
    } else {
      total = this.basket?.productSubTotal || 0
    }
    return total
  }

  get mergeBasketCount() {
    let count = 0
    if (this.basketMergeType !== 'edit') {
      if (this.basket?.c_storedBasketJSON?.length) {
        try {
          const storedBasket = JSON.parse(this.basket?.c_storedBasketJSON)
          if ('productLineItems' in storedBasket && storedBasket.productLineItems.length) {
            for (const x in storedBasket.productLineItems) {
              count += storedBasket.productLineItems[x].quantity
            }
          }
        } catch (e) {
          count = 0
        }
      }
    } else {
      count = this.count || 0
    }
    return count
  }

  get restrictedProductClasses() {
    const restrictedProductClassesCustomPref = this.rootStore.globalStore.customSitePreferences['restrictedProductsJSON'] as string
    let restrictedProductClassesJSON: { [key: string]: { limit: number } } = {}
    if (restrictedProductClassesCustomPref) {
      try {
        restrictedProductClassesJSON = JSON.parse(restrictedProductClassesCustomPref)
      } catch (error) {
        console.error('Error parsing restrictedProductsJSON:', error)
      }
    }
    return restrictedProductClassesJSON
  }

  get previouslyRemovedItems(): PreviouslyRemovedItem[] | [] {
    let prevItems = this.basket?.c_previouslyRemovedItems
    if (typeof prevItems === 'string') {
      prevItems = JSON.parse(this.basket?.c_previouslyRemovedItems) || []
      return prevItems
    } else if (Array.isArray(prevItems)) {
      return prevItems
    }
    return []
  }

  /**
   * Checks for and applies restrictions to a specified product when restrictions are specified(productClasses)
   * @param productClasses an array of product classes for restrictions to process
   * @param productId the ID of the product to check if restricted
   * @returns {Object} an object containing data on whether this product is restricted
   */
  getRestrictedProductInfo(productClasses: string[], productId?: string) {
    const productStore = this.rootStore.productStore
    const isProductRestricted = this.checkIfProductIsRestricted(productClasses)
    const restrictedProductLimit = this.findRestrictedProductLimit(productClasses)
    const productData = productId ? productStore.productsById[productId] : undefined
    const maxQuantityAllowed: number = productData && restrictedProductLimit != null && productData.maxOrderQuantity != null
      ? Math.min(restrictedProductLimit, productData.maxOrderQuantity)
      : restrictedProductLimit ?? (productData ? productData.maxOrderQuantity : undefined)
    const cannotAddToBasket = maxQuantityAllowed === 0 || (this.hasReachedRestrictedProductClassLimit(productClasses) && isProductRestricted) || false

    return {
      isProductRestricted,
      restrictedProductLimit,
      cannotAddToBasket,
      maxQuantityAllowed
    }
  }

  /**
   * @desc Check if a product has a restricted product class
   * @param {string[]} productClasses an array of product classes to check if they are restricted
   * @returns {boolean} true if one of the provided classes is restricted, false otherwise
   */
  checkIfProductIsRestricted(productClasses: string[]): boolean {
    if (productClasses) {
      const restrictedProductClassesKeys = Object.keys(this.restrictedProductClasses)
      for (let i = 0; i < productClasses.length; i++) {
        if (restrictedProductClassesKeys.includes(productClasses[i])) {
          return true
        }
      }
    }
    return false
  }

  /**
   * @desc Find the limit for the restricted product class
   * @param {string[]} productClasses an array of product classes to check if they are restricted
   * @returns {number | null} returns null if no limit, otherwise returns the limit for that restriction
   */
  findRestrictedProductLimit(productClasses: string[]): number | null {
    if (productClasses) {
      const restrictedProductClassesKeys = Object.keys(this.restrictedProductClasses)
      for (let i = 0; i < productClasses.length; i++) {
        if (restrictedProductClassesKeys.includes(productClasses[i])) {
          const limit = this.restrictedProductClasses[productClasses[i]].limit
          return limit
        }
      }
    }
    return null
  }

  hasReachedRestrictedProductClassLimit(productClasses: string[]): boolean | undefined {
    const productStore = this.rootStore.productStore
    const productsInBasket = this.basket?.productItems
    const totalRestrictedQuantities: { [key: string]: { total: number, limitReached: boolean } } = {}

    // Initialize the total quantities and limitReached flag for each restricted product class
    Object.keys(this.restrictedProductClasses).forEach((productClass) => {
      totalRestrictedQuantities[productClass] = { total: 0, limitReached: false }
    })

    // Calculate total quantities for each restricted product class
    productsInBasket?.forEach((product) => {
      const productData = product.productId ? productStore.productsById[product.productId] : undefined

      productData?.productClasses?.forEach((productClass: string) => {
        if (Object.keys(this.restrictedProductClasses).includes(productClass)) {
          totalRestrictedQuantities[productClass].total += product.quantity ?? 0

          // Check if the total exceeds the limit for this product class
          const productLimit = this.findRestrictedProductLimit([productClass])
          if (productLimit !== null && totalRestrictedQuantities[productClass].total >= productLimit) {
            totalRestrictedQuantities[productClass].limitReached = true
          }
        }
      })
    })

    // Check if any restricted product class has reached its limit
    return Array.isArray(productClasses) && productClasses.some((productClass) => {
      const productLimit = this.findRestrictedProductLimit([productClass])
      const restrictedQuantity = totalRestrictedQuantities[productClass]?.total
      return productLimit !== null && restrictedQuantity !== undefined && restrictedQuantity >= productLimit
    })
  }

  runATBAnalytics(items: PartialProductItem[]) {
    /** Einstein Analytics events **/
    this.api.einstein?.sendAddToCart({
      products: items.map((item) => {
        return {
          id: item.product?.id,
          quantity: item.quantity,
          price: item.product?.price ?? 0,
          sku: '',
        }
      }),
    })

    items.forEach((item) => {
      const { product, event } = item
      const queryID = product?.__queryID || product?.__autocomplete_queryID
      const fromSearchResult = Boolean(queryID)
      const settings = {
        method: fromSearchResult
          ? 'convertedObjectIDsAfterSearch'
          : ('convertedObjectIDs' as keyof InsightsMethodMap),
        params: {
          objectIDs: [product?.id],
          ...(fromSearchResult && { queryID: queryID }),
        },
      }
      const adId = item?.product?.adId
      if (item?.product?.isSponsored && adId) {
        sendRMClicks(adId, this.rootStore.retailMediaStore.retailMediaUrl)
        }

      /* Algolia Analytics events */
      if (event) {
        AlgoliaAnalytics(settings.method, {
          eventName: event,
          index: `${this.rootStore.globalStore.algoliaIndexBase}__products__default`,
          userToken: this.rootStore.customerStore.customerInfo?.customerId,
          ...settings.params,
        })
      }
    })
  }

  slotTime(dateStrUtc: string) {
    if (!dateStrUtc) return ''

    const zonedDate = DateUtils.utcToZonedDate(dateStrUtc)

    if (!DateUtils.validateDate(zonedDate)) {
      return ''
    }

    return format(zonedDate, 'HH:mm')
  }

  setIsAppliedPostCodeDeliverableValue(value: Nullable<boolean>) {
    this.isAppliedPostCodeDeliverable = value
  }

  setBasketMergeType(value: Nullable<BasketMergeTypes>) {
    this.basketMergeType = value
  }

  setStatus(value: Status) {
    this.status = value
  }

  setDeliverySlotStatus(value: Status) {
    this.deliverySlotStatus = value
  }

  setInitMergingUI(value: boolean) {
    this.initMergingUI = value
  }

  setBasketMergeData(value: string) {
    this.basketMergeData = value
  }

  setOpenMiniBasketUI(open: boolean) {
    this.openMiniBasketUI = open
  }

  setPostcodeError(error: boolean) {
    this.postcodeError = error
  }

  setBonusCardStatus(status: CheckoutBonusCardStatus) {
    this.bonusCardStatus = status
  }

  setNearestStore(storeDetail: StoreDetailResponseItem | null) {
    this.nearestStoreByDeliveryPostCode = storeDetail
  }

  setProductsToUpdate(value: PartialProductItem[]) {
    this.productsToUpdate = value
  }

  addProductToUpdate(product: PartialProductItem) {
    this.productsToUpdate.push(product)
  }

  setPendingProducts(productIds: Array<PartialProductItem>) {
    this.pendingProducts = productIds
  }

  setPendingATBEvent(pendingATBEvent: Nullable<ATBEvent>) {
    this.pendingATBEvent = pendingATBEvent
  }

  setPendingPromoModal(promo: Promotion | undefined) {
    this.pendingPromo = promo
  }

  setMissedOffers(value: Nullable<MissedOffers>) {
    this.missedOffers = value
  }

  setDeliverySlots(value: Nullable<DeliverySlot>) {
    this.deliverySlots = value
  }

  setNextAvailableSlot(value: Nullable<TimeSlot>) {
    this.nextAvailableSlot = value
  }

  setDeliverySlotPostalCode(value: Nullable<string>) {
    this.deliverySlotPostalCode = value
  }

  setBeforeYouGoPages(value: Nullable<BeforeYouGoPages>) {
    this.beforeYouGoPages = value
  }

  setPromotionalThresholds(value: BasketPromotionsThreshold[]) {
    this.promotionalThresholds = value
  }

  setFilteredPromotionalThresholds(value: BasketPromotionsThreshold[]) {
    this.filteredPromotionalThresholds = value
  }

  setProcessingAppliedPostCode(value: boolean) {
    this.processingAppliedPostCode = value
  }

  async setIsAppliedPostCodeDeliverable(postcode: string) {
    const postcodeDeliveryStatus = await flowResult(
      this.handlePostcodeDeliveryStatus(postcode)
    )
    this.setIsAppliedPostCodeDeliverableValue(postcodeDeliveryStatus)
  }

  setBasket(value: ShopperBaskets.Basket | StoreBasket | undefined, alreadyTransfomed?: boolean) {
    let basket = value
    if (value && !alreadyTransfomed) {
      basket = transformBasket(this, value)
    }
    
    if (basket && !basket.c_deliveryPostalCode && this.basket?.c_deliveryPostalCode) {
      basket.c_deliveryPostalCode = this.basket.c_deliveryPostalCode
    }
    this.basket = basket as StoreBasket | undefined
  }

  updateProductToUpdateProductQuantity(productToUpdateId: string | undefined, quantity: number) {
    if (!productToUpdateId) {
      return
    }
    const newProductsArray = this.productsToUpdate.map((product) => {
      if (product.product?.id === productToUpdateId) {
        return {
          ...product,
          quantity,
        }
      }
      return product
    })

    this.setProductsToUpdate(newProductsArray)
  }

  getUpdateProductIndex(productId: string | undefined) {
    if (!productId) {
      return -1
    }
    return this.productsToUpdate.findIndex((prod) => prod.product?.id === productId)
  }

  *updateBasketItemQuantity(
    args: PartialProductItem | PartialProductItem[],
    openMiniBasket?: boolean
  ) {
    const productsForAnalytics: PartialProductItem[] = []
    const bonusLineItemAmount = this.bonusLineItemAmount

    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    // If we're already pending a request, no need to fire again
    if (this.status === StatusMap.PENDING) {
      return
    }

    this.setStatus(StatusMap.PENDING)

    // Allow method to accept both single or multiple (array) items
    const items = Array.isArray(args) ? args : [args]

    const isBasketEmptyBeforeUpdate = this.basket.productItems?.length === 0
    const addedFromSearchDropdown = isBasketEmptyBeforeUpdate
      ? items.find((item) => item.isSearch)
      : false

    try {
      const itemPayload = items
        .filter((item) => item.product)
        .map((item) => {
          const existing = this.basket?.productItems?.find((basketItem) => {
            return basketItem.productId === item.product?.id
          })
          if (!existing) {
            productsForAnalytics.push(item)
          }
          return {
            productId: item.product?.id,
            quantity: item.quantity,
          }
        })

      const itemsToAdd = {
        body: itemPayload,
      }
      const result: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-updateBasketItemQuantity`,
        {
          method: 'POST',
          body: JSON.stringify(itemsToAdd.body),
        }
      )
      if (!result.ok) throw new Error('Product not available to add to basket')

      const json: {
        success: boolean
        basket: ShopperBaskets.Basket
        incompleteOffers: MissedOffers
      } = yield result.json()

      this.setStatus(StatusMap.IDLE)

      // After we successfully added 1st item in the empty cart we need to open drawer.
      if (json.success) {
        // Update the products in the basket
        this.getOrCreateBasket(json.basket, json.incompleteOffers)

        const newBonusLineItemAmount = this.bonusLineItemAmount

        // Run analytics if ATB was successful and a new product has been added
        if (productsForAnalytics.length) {
          this.runATBAnalytics(productsForAnalytics)
        }

        // Trigger notification if bonusLineItem is added
        if (newBonusLineItemAmount > bonusLineItemAmount) {
          this.rootStore.alertsStore?.triggerNotification({
            message: 'Free gift added to your basket!',
            type: NotificationType.success,
            autoClose: true
          })
        }

        if (
          this.basket.productItems?.length === 1 &&
          isBasketEmptyBeforeUpdate
        ) {
          if (!openMiniBasket &&
            !addedFromSearchDropdown
          ) {
            this.setOpenMiniBasketUI(true)
          }

        }
        // Clear recent order
        this.rootStore.orderStore.setRecentOrderValue(null)
      }

      return json
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *removePreviouslyRemovedItem(itemId: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    try {
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-RemovePreviouslyRemovedItem?pid=${itemId}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      )

      if (!res.ok) {
        throw new Error('Error removing previously saved item')
      } else {
        const json: { basket?: ShopperBaskets.Basket } = yield res.json()
        if (json?.basket) {
          this.getOrCreateBasket(json.basket)
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  *clearPreviouslyRemovedItems() {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    try {
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-ClearPreviouslyRemovedItems`,
        {
          method: 'GET',
          credentials: 'include',
        }
      )

      if (!res.ok) {
        throw new Error('Error clearing previously removed items')
      } else {
        const json: { basket?: ShopperBaskets.Basket } = yield res.json()
        if (json?.basket) {
          this.getOrCreateBasket(json.basket)
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  // Fetches basket from the backend - Used for getOrCreateBasket only DO NOT USE outside of that
  *getBasket() {
    // TODO: Update to proper fetch basket endpoint
    const result: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-updateBasketItemQuantity`,
      {
        method: 'POST',
        body: JSON.stringify([]),
      }
    )

    const json: {
      basket: ShopperBaskets.Basket
      incompleteOffers: MissedOffers
    } = yield result.json()

    return json
  }

  /**
   * Fetches customer baskets and stores the most recent. If none exist, a new basket is created.
   */
  *getOrCreateBasket(basket?: ShopperBaskets.Basket, incompleteOffers?: MissedOffers, customerId = this.rootStore.customerStore?.customerInfo?.customerId || undefined) {
    try {
      if (!customerId) {
        throw new Error('Missing customer ID')
      }
      // Make sure basketMergeData is cleared when the basket is set
      this.setBasketMergeData('')

      let customerBasket: ShopperBaskets.Basket | BasketResult | undefined = basket
      let missedOffers: MissedOffers | undefined = incompleteOffers

      // Fetch basket if missing
      if (!customerBasket) {
        var {basket: fetchedBasket, incompleteOffers: fetchedOffers} = yield this.getBasket()
        customerBasket = fetchedBasket
        missedOffers = fetchedOffers
      }

      let basketToReturn
      if (customerBasket?.basketId) {
        basketToReturn = customerBasket
        // this.getMissedOffers(incompleteOffers) // No need to go fetch this stuff again if we've just had a response from the BE for it already
        if(incompleteOffers) {
          this.setMissedOffers(missedOffers ?? null)
        }

      } else {
        // Customer has no baskets, create one and save it
        const newBasket: CustomerBasket = yield this.api.shopperBaskets.createBasket({ body: {} })
        basketToReturn = newBasket
      }

      this.setBasket(basketToReturn)

      // Set some background variables here for PriceBooks
      // Set delivery available flag
      yield this.setIsAppliedPostCodeDeliverable(this.deliveryPostalCode)
      // Set SelectedStoreId
      this.rootStore.globalStore.setSelectedStoreId()
      // Set the selected Stores Region
      this.rootStore.globalStore.setSelectedRegion()
      // Set price day
      this.rootStore.globalStore.setSelectedPriceDay()
      // Set Availability day
      this.rootStore.globalStore.setSelectedAvailabilityDay()
      // Set some background variables here for PriceBooks

      // Ensure the selected app currency matches the basket currency
      const currency = this.rootStore.globalStore.currency

      if (currency && this.basket?.currency !== currency && this.basket?.basketId) {
        yield this.updateBasketCurrency(currency, this.basket.basketId)
      }

      yield this.getBasketPromotionalThreshold()

      // Basket is loaded, so UI can render most of the basket now
      this.setStatus(StatusMap.IDLE)

      // Kick off a fetch of product details for each item. We need this to render
      // a cart item in the basket, since the basket item does not include an image
      // or any information about the variation values.
      if (this.basket?.productItems) {
        const productIds = this.allProductLineItems.map((item) => item.productId || '')
        this.rootStore.productStore.fetchProducts(productIds, true)
      }

      if (this.previouslyRemovedItems.length) {
        const prevRemovedIds = this.previouslyRemovedItems.map(
          (item: PreviouslyRemovedItem) => item.pid || ''
        )
        this.rootStore.productStore.fetchProducts(prevRemovedIds, false)
      }

      // Once we created a new basket we don't have shipment address,
      // calling OCAPI to set primary saved user's address.
      if (!this.shippingAddress && this.rootStore.customerStore.isRegistered) {
        if (this.rootStore.customerStore.addresses?.length) {
          const shippingAddress = this.rootStore.customerStore.addresses.find(
            (address) => address.preferred
          )
          if (shippingAddress)
            yield this.addOrUpdateShippingAddress(shippingAddress as OrderAddress, false)
        }
      }
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *updateBasketCurrency(currency: string, basketId: string) {
    try {
      const updatedBasket: CustomerBasket = yield this.api.shopperBaskets.updateBasket({
        body: { currency },
        parameters: { basketId },
      })

      this.setBasket(updatedBasket)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *updateBasketDeliveryNote(note: string) {
    try {
      // If no change don't attempt to call endpoint
      if (note === this.deliveryNotes) {
        return
      }

      const body = {
        deliveryNotes: note
      }

      const formBody = []
      for (const property in body) {
        const encodedKey = encodeURIComponent(property)
        const encodedValue = encodeURIComponent(
          body[property as keyof typeof body] as string | number
        )
        formBody.push(encodedKey + '=' + encodedValue)
      }


      const result: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-UpdateDeliveryNotes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body: formBody.join('&')
        }
      )

      if (!result.ok) {
        throw new Error('Error updating delivery notes')
      }

      const resultJson: { success: boolean, basket: ShopperBaskets.Basket } = yield result.json()

      this.status = 'idle'

      if (resultJson.success) {
        this.getOrCreateBasket(resultJson.basket)
      } else {
        this.status = 'error'
        throw new Error('Error - Delivery notes not updated')
      }


      return resultJson
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *updateBasketSubstitutes(excludeSubstitutes: boolean) {
    try {
      const updatedBasket: CustomerBasket = yield this.api.shopperBaskets.updateBasket({
        body: { c_excludeSubstitutes: excludeSubstitutes },
        parameters: { basketId: this.basket?.basketId },
      })

      this.setBasket(updatedBasket)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *updateCarrierBag(includeBag = true) {
    try {
      const updatedBasket: CustomerBasket = yield this.api.shopperBaskets.updateBasket({
        body: { c_isCarrierBagAdded: includeBag },
        parameters: { basketId: this.basket?.basketId },
      })
      this.setBasket(updatedBasket)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *retrieveParkedCartBack() {
    if (!LocalStorage.available || !this.basket) return

    try {
      let basketToReturn: StoreBasket = {}
      this.setStatus(StatusMap.PENDING)
      const existedParkedBasket = LocalStorage.get(LOCALSTORAGE_KEYS.PARKED_CUSTOMER_CART)

      if (existedParkedBasket) {
        const { basketId, c_orderNoBeingEdited, ...basket } = yield JSON.parse(existedParkedBasket)

        yield this.api.shopperBaskets.deleteBasket({
          parameters: {
            basketId: this.basket.basketId!,
          },
        })

        const newBasket: StoreBasket = yield this.api.shopperBaskets.createBasket({
          parameters: {},
          // We shouldn't get editMode parked basket, so create an empty one, otherwise use parked basket properties.
          body: c_orderNoBeingEdited ? {} : basket,
        })

        basketToReturn = transformBasket(this, {
          ...newBasket,
        })

        if (basketToReturn.productItems?.length) {
          this.rootStore.productStore.fetchProducts(
            basketToReturn.productItems
              .map((product) => product.productId)
              .filter((id): id is string => !!id)
            , false)
        }

        LocalStorage.remove(LOCALSTORAGE_KEYS.PARKED_CUSTOMER_CART)
        // If User cleared session and parked basket is absent, creating an empty one
      } else {
        yield this.api.shopperBaskets.deleteBasket({
          parameters: {
            basketId: this.basket.basketId!,
          },
        })
        const emptyBasket: StoreBasket = yield this.api.shopperBaskets.createBasket({
          parameters: {},
          body: {},
        })
        basketToReturn = transformBasket(this, emptyBasket)
      }

      this.setBasket(basketToReturn, true)
      this.setStatus(StatusMap.DONE)

      return basketToReturn
    } catch (error) {
      console.error("Error has occurred during getting parked user's basket.")
      this.setStatus(StatusMap.ERROR)
    }
  }

  *checkoutLogin(credentials: { email: string; password?: string }) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.setStatus(StatusMap.PENDING)
    try {
      if ('password' in credentials) {
        yield this.rootStore.customerStore.loginCustomer(credentials as CustomerCredentials)
      }
      const result: CustomerBasket = yield this.api.shopperBaskets.updateCustomerForBasket({
        body: { email: credentials.email },
        parameters: { basketId: this.basket?.basketId },
      })
      this.setBasket(result)
      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *updateCustomerInfo(info: ShopperBaskets.CustomerInfo) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    const result: CustomerBasket = yield this.api.shopperBaskets.updateCustomerForBasket({
      body: info,
      parameters: { basketId: this.basket.basketId },
    })
    this.setBasket(result)
  }

  *addOrUpdateBillingAddress(address: OrderAddress) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.setStatus(StatusMap.PENDING)
    const {
      id,
      preferred,
      creationDate,
      lastModified,
      addressId,
      addressName,
      c_updateHistory,
      _resource_state,
      __type,
      _v,
      ...body
    } = address
    try {
      const result: CustomerBasket = yield this.api.shopperBaskets.updateBillingAddressForBasket({
        body,
        parameters: { basketId: this.basket?.basketId },
      })
      this.setBasket({ ...this.basket, ...result })
      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *addOrUpdateShippingAddress(address: OrderAddress, saveAddress = true) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.setStatus(StatusMap.PENDING)
    try {
      const {
        id,
        preferred,
        creationDate,
        lastModified,
        addressId,
        addressName,
        c_updateSource,
        c_updateHistory,
        _resource_state,
        __type,
        _v,
        ...body
      } = address
      const result: CustomerBasket = yield this.api.shopperBaskets.updateShippingAddressForShipment(
        {
          body: {
            ...body,
            /* We need c_customerAddressID for association with customer's address,
            because shipping one has auto-generated server side id */
            c_customerAddressID: addressId,
          },
          parameters: {
            basketId: this.basket?.basketId,
            shipmentId: 'me',
            useAsBilling: false,
          },
        }
      )
      this.setBasket({ ...this.basket, ...result })
      this.getShippingMethods()

      if (this.rootStore.customerStore?.isRegistered && saveAddress) {
        if (!addressId) return
        const existedAddress = this.rootStore.customerStore.addresses.find(
          (customerAddress) => customerAddress.addressId === addressId
        )

        if (existedAddress) {
          yield this.rootStore.customerStore.updateCustomerAddress({
            ...body,
            addressId,
            preferred,
            c_updateSource,
          } as ShopperCustomers.CustomerAddress)
        } else {
          yield this.rootStore.customerStore.createCustomerAddress({
            ...body,
            preferred,
            addressId,
            c_updateSource,
          } as ShopperCustomers.CustomerAddress)
        }
      }
      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *getShippingMethods() {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.setStatus(StatusMap.PENDING)
    try {
      const result: ShopperBaskets.ShippingMethodResult =
        yield this.api.shopperBaskets.getShippingMethodsForShipment({
          parameters: { basketId: this.basket?.basketId, shipmentId: 'me' },
        })

      if (this.basket) {
        this.basket.applicableShippingMethods = result.applicableShippingMethods
        this.basket.defaultShippingMethodId = result.defaultShippingMethodId
      }

      if (
        result.applicableShippingMethods &&
        result.applicableShippingMethods[0] &&
        !this.shippingMethod
      ) {
        this.setShippingMethod(result.applicableShippingMethods[0].id)
      }
      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *setShippingMethod(id: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.setStatus(StatusMap.PENDING)
    try {
      const result: CustomerBasket = yield this.api.shopperBaskets.updateShippingMethodForShipment({
        body: { id },
        parameters: { basketId: this.basket?.basketId, shipmentId: 'me' },
      })
      this.setBasket({ ...this.basket, ...result })
      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *submitOrder(
    submissionData: SubmitOrderData,
    paymentType: Nullable<string>
  ) {
    this.setStatus(StatusMap.PENDING)

    const {
      editMode = false,
      orderEditOrderTotal,
      orderTotalWithoutBC,
      isPaymentRefunding = false,
      setRecentOrder,
      setPopulateOrderFromRecentOrder
    } = this.rootStore.orderStore

    const {
      btData,
      userData,
      threeDSecureData,
      usingSavedCard,
      saveCreditCard,
      saveNewAddress,
      billingAddress,
      token
    } = submissionData

    try {
      const paymentData = {
        btData,
        userData,
        threeDSecureData,
        paymentType,
        usingSavedCard,
        billingAddress,
      }

      const { hidePayment } = this.bonusCardStatus

      const body = {
        dwfrm_billing_save: false,
        dwfrm_billing_addressList: '',
        dwfrm_billing_billingAddress_addressFields_title: billingAddress?.title,
        dwfrm_billing_billingAddress_addressFields_firstName: billingAddress?.firstName,
        dwfrm_billing_billingAddress_addressFields_lastName: billingAddress?.lastName,
        dwfrm_billing_billingAddress_addressFields_customerAddressID: billingAddress?.addressId,
        dwfrm_billing_billingAddress_addressFields_postal: billingAddress?.postalCode,
        dwfrm_billing_billingAddress_addressFields_address1: billingAddress?.address1,
        dwfrm_billing_billingAddress_addressFields_address2: billingAddress?.address2,
        dwfrm_billing_billingAddress_addressFields_city: billingAddress?.city,
        dwfrm_billing_billingAddress_addressFields_county: billingAddress?.stateCode,
        dwfrm_billing_billingAddress_addressFields_phone: billingAddress?.phone?.replace(' ', ''),
        dwfrm_billing_billingAddress_addToAddressBook: saveNewAddress ?? false,
        deviceType: isServer ? null : getDeviceType(window?.navigator?.userAgent),
        utmMedium: isServer ? null : window.sessionStorage?.getItem('affiliateTrackingMedium'),
        utmSource: isServer ? null : window.sessionStorage?.getItem('affiliateTrackingSource'),
        utmCampaign: isServer ? null : window.sessionStorage?.getItem('affiliateTrackingCampaign'),
        fraudDeviceData: paymentData?.btData?.fraudDeviceData || 'empty', //todo: remove
        dwfrm_billing_billingAddress_email_emailAddress:
          this.basket?.customerInfo?.email || this.rootStore.customerStore.customerInfo?.email,
        dwfrm_billing_billingAddress_addressFields_country: 'gb',
        paymentAmount: orderTotalWithoutBC,
        customerNo: this.basket?.customerInfo?.customerNo || '',
        ...((hidePayment || isPaymentRefunding || (editMode && orderEditOrderTotal === 0)) && {
          noPaymentNeeded: true,
        }),
        ...(paymentType === PAYMENT_METHOD_IDS.PAYPAL && {
          paypalReferrer: getAppOrigin().replace('http://', '').replace('https://', ''),
          braintreePaypalNonce: paymentData?.btData?.nonce,
          braintreePaypalEmail: paymentData?.btData?.details?.accountEmail,
        }),
        paymentMethodToken: token,
        ...(!isPaymentRefunding && {
          braintreePaymentMethodNonce:
            paymentData?.threeDSecureData?.nonce || paymentData?.btData?.nonce || '',
          braintreePaymentMethodBillingAddress: JSON.stringify(billingAddress),
          ...(!hidePayment && {
            dwfrm_billing_paymentMethods_selectedPaymentMethodID: paymentData?.paymentType,
          })
        }),
        ...((!isPaymentRefunding && paymentType !== PAYMENT_METHOD_IDS.PAYPAL && paymentType !== PAYMENT_METHOD_IDS.KLARNA)
          ? {
            braintreeUseSavedCard: paymentData?.usingSavedCard,
            braintreeSaveCreditCard: saveCreditCard ?? false,
            // TODO value needs to be set by 'Make default' input - should be visible when checking out with new card & vault mode turned on
            braintreeCreditCardMakeDefault: false,
            dwfrm_creditcard_owner: '',
            dwfrm_creditcard_type: paymentData?.threeDSecureData?.details?.cardType || '',
            dwfrm_creditcard_number: `************${paymentData?.threeDSecureData?.details.lastFour}`,
            braintreeDeviceData: JSON.stringify(paymentData?.userData),
            dwfrm_billing_paymentMethods_creditCard_type:
              paymentData?.threeDSecureData?.details?.cardType || '',
            dwfrm_billing_paymentMethods_creditCard_number: paymentData?.threeDSecureData?.details?.lastFour,
            dwfrm_billing_paymentMethods_creditCard_owner: '',
            'cc-expiration-date': '',
            dwfrm_billing_paymentMethods_creditCard_expiration_month: '',
            dwfrm_billing_paymentMethods_creditCard_expiration_year: '',
            dwfrm_billing_paymentMethods_creditCard_cvn: ''
          }
          : {}),
      }

      const formBody = []
      for (const property in body) {
        const encodedKey = encodeURIComponent(property)
        const encodedValue = encodeURIComponent(
          body[property as keyof typeof body] as string | number
        )
        formBody.push(encodedKey + '=' + encodedValue)
      }

      const result: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Checkout-CreateOrder`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body: formBody.join('&'),
        }
      )

      if (!result.ok) {
        throw new Error('Error during placing the order')
      }

      const resultJson: { order_created: boolean, basket: ShopperBaskets.Basket, [key: string]: unknown, c_customerGroups: string[], Order: OrderResponse, payments?: PaymentSummaries[], error: boolean, errorType: string } = yield result.json()

      // Reset bonus card input
      this.setBonusCardStatus({})
      this.setStatus(StatusMap.IDLE)
      this.getOrCreateBasket(resultJson.basket)
      if (resultJson.order_created) {
        if (resultJson.Order?.data) {
          setPopulateOrderFromRecentOrder(true)
          setRecentOrder(resultJson.Order?.data)
        }
        if (resultJson.c_customerGroups) {
          this.rootStore.customerStore.setCustomerGroups(resultJson.c_customerGroups)
        }
      }
      return resultJson
    } catch (error) {
      console.error(error)
    }
  }

  *submitPreBookPurchase(
    submissionData: SubmitOrderData,
    paymentType: Nullable<string>
  ) {
    this.setStatus(StatusMap.PENDING)

    const {
      editMode = false,
      orderEditOrderTotal,
      orderTotalWithoutBC,
      isPaymentRefunding = false,
    } = this.rootStore.orderStore

    const {
      btData,
      userData,
      threeDSecureData,
      usingSavedCard,
      saveCreditCard,
      saveNewAddress,
      billingAddress,
      klarnaData,
    } = submissionData

    try {
      const paymentData = {
        btData,
        userData,
        threeDSecureData,
        paymentType,
        usingSavedCard,
        billingAddress,
        klarnaData,
      }

      const {hidePayment} = this.bonusCardStatus

      const body = {
        dwfrm_billing_save: false,
        dwfrm_billing_addressList: '',
        dwfrm_billing_billingAddress_addressFields_title: billingAddress?.title,
        dwfrm_billing_billingAddress_addressFields_firstName: billingAddress?.firstName,
        dwfrm_billing_billingAddress_addressFields_lastName: billingAddress?.lastName,
        dwfrm_billing_billingAddress_addressFields_customerAddressID: billingAddress?.addressId,
        dwfrm_billing_billingAddress_addressFields_postal: billingAddress?.postalCode,
        dwfrm_billing_billingAddress_addressFields_address1: billingAddress?.address1,
        dwfrm_billing_billingAddress_addressFields_address2: billingAddress?.address2,
        dwfrm_billing_billingAddress_addressFields_city: billingAddress?.city,
        dwfrm_billing_billingAddress_addressFields_county: billingAddress?.stateCode,
        dwfrm_billing_billingAddress_addressFields_phone: billingAddress?.phone?.replace(' ', ''),
        dwfrm_billing_billingAddress_addToAddressBook: saveNewAddress ?? false,
        deviceType: isServer ? null : getDeviceType(window?.navigator?.userAgent),
        utmMedium: isServer ? null : window.sessionStorage?.getItem('affiliateTrackingMedium'),
        utmSource: isServer ? null : window.sessionStorage?.getItem('affiliateTrackingSource'),
        utmCampaign: isServer ? null : window.sessionStorage?.getItem('affiliateTrackingCampaign'),
        dwfrm_billing_billingAddress_email_emailAddress:
          this.basket?.customerInfo?.email || this.rootStore.customerStore.customerInfo?.email,
        dwfrm_billing_billingAddress_addressFields_country: 'gb',
        dwfrm_billing_paymentMethods_selectedPaymentMethodID: paymentData?.paymentType,
        paymentAmount: orderTotalWithoutBC,
        customerNo: this.basket?.customerInfo?.customerNo || '',
        ...(paymentType === PAYMENT_METHOD_IDS.PAYPAL && {
          paypalReferrer: getAppOrigin().replace('http://', '').replace('https://', ''),
          braintreePaypalNonce: paymentData?.btData?.nonce,
          braintreePaypalEmail: paymentData?.btData?.details?.accountEmail,
        }),
        ...(paymentType === PAYMENT_METHOD_IDS.KLARNA && {
          klarnaData: JSON.stringify({
            showForm: paymentData?.klarnaData?.show_form,
            approved: paymentData?.klarnaData?.approved,
            finalizeRequired: paymentData?.klarnaData?.finalize_required,
            authorizationToken: paymentData?.klarnaData?.authorization_token,
            clientToken: paymentData?.klarnaData?.client_token,
            sessionId: paymentData?.klarnaData?.session_id
          })
        }),
        ...(!isPaymentRefunding && {
          braintreePaymentMethodNonce:
            paymentData?.threeDSecureData?.nonce || paymentData?.btData?.nonce || '',
          braintreePaymentMethodBillingAddress: JSON.stringify(billingAddress),
        }),
        ...((!isPaymentRefunding && paymentType !== PAYMENT_METHOD_IDS.PAYPAL && paymentType !== PAYMENT_METHOD_IDS.KLARNA)
          ? {
            braintreeUseSavedCard: paymentData?.usingSavedCard,
            braintreeSaveCreditCard: saveCreditCard ?? false,
            // TODO value needs to be set by 'Make default' input - should be visible when checking out with new card & vault mode turned on
            braintreeCreditCardMakeDefault: false,
            dwfrm_creditcard_owner: '',
            dwfrm_creditcard_type: paymentData?.threeDSecureData?.details?.cardType || '',
            dwfrm_creditcard_number: `************${paymentData?.threeDSecureData?.details.lastFour}`,
            braintreeDeviceData: JSON.stringify(paymentData?.userData),
            dwfrm_billing_paymentMethods_creditCard_type:
              paymentData?.threeDSecureData?.details?.cardType || '',
            dwfrm_billing_paymentMethods_creditCard_number: paymentData?.threeDSecureData?.details?.lastFour,
            dwfrm_billing_paymentMethods_creditCard_owner: '',
            'cc-expiration-date': '',
            dwfrm_billing_paymentMethods_creditCard_expiration_month: '',
            dwfrm_billing_paymentMethods_creditCard_expiration_year: '',
            dwfrm_billing_paymentMethods_creditCard_cvn: ''
          }
          : {}),
      }

      const formBody = []
      for (const property in body) {
        const encodedKey = encodeURIComponent(property)
        const encodedValue = encodeURIComponent(
          body[property as keyof typeof body] as string | number
        )
        formBody.push(encodedKey + '=' + encodedValue)
      }

      const result: {success: boolean, error: string, order: ShopperCustomers.Order | null} = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Checkout-PreBookSlot`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'},
          body: formBody.join('&'),
        }
      ).then(async (res) => {
          if (!res.ok) {
            throw new Error('Error during placing the order')
          }
          const resultJson: {success: boolean} = await res.json()
          if (resultJson.success) {
            // Get updated customer
            await this.rootStore.customerStore.getCustomer()
          }

        return resultJson
      })

      this.setStatus(StatusMap.IDLE)

      return {...result, order: this.rootStore.preBookStore.prebookedOrder }
    } catch (error) {
      console.error(error)
    }
  }

  *setCustomerPaymentMethod(paymentMethod: ShopperBaskets.BasketPaymentInstrumentRequest) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    const { paymentInstrumentId, ...data } = paymentMethod
    let result: CustomerBasket
    this.setStatus(StatusMap.PENDING)
    const existingPaymentInstrumentId =
      this.basket?.paymentInstruments && this.basket.paymentInstruments[0]?.paymentInstrumentId

    let paymentInstrument

    if (!paymentInstrumentId) {
      const expiry = data?.expiry?.split('/')
      const expirationMonth = expiry?.[0]
      const expirationYear = expiry?.[1]
      delete data.expiry

      paymentInstrument = {
        paymentMethodId: PAYMENT_METHOD_IDS.CREDIT_CARD,
        paymentCard: {
          ...data,

          // /** @todo - this field does not exist in the request type, using maskedNumber for now */
          number: data.number?.replace(/ /g, ''),
          // maskedNumber: data.number?.replace(/ /g, ''),

          cardType: data.cardType,
          expirationMonth: parseInt(expirationMonth as string),
          expirationYear: parseInt(`20${expirationYear}`),

          // TODO: These fields are required for saving the card to the customer's
          // account. Im not sure what they are for or how to get them, so for now
          // we're just passing some values to make it work. Need to investigate.
          issueNumber: '',
          validFromMonth: 1,
          validFromYear: 2020,
        },
      }
    } else {
      paymentInstrument = {
        customerPaymentInstrumentId: paymentInstrumentId,
      }
    }

    try {
      result = yield this.api.shopperBaskets.addPaymentInstrumentToBasket({
        body: paymentInstrument,
        parameters: { basketId: this.basket?.basketId },
      })
      if (existingPaymentInstrumentId) {
        result = yield this.api.shopperBaskets.removePaymentInstrumentFromBasket({
          parameters: {
            basketId: this.basket?.basketId,
            paymentInstrumentId: existingPaymentInstrumentId,
          },
        })
      }
      if (this.rootStore.customerStore?.isRegistered && !paymentInstrumentId) {
        this.rootStore.customerStore.addCustomerPaymentInstrument(
          paymentInstrument as ShopperCustomers.CustomerPaymentInstrumentRequest
        )
      }
      if (this.basket) {
        this.basket.paymentInstruments = result.paymentInstruments as PaymentInstrument[]
      }
      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *deleteCustomerPaymentMethod(paymentInstrumentId: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.setStatus(StatusMap.PENDING)
    try {
      const result: CustomerBasket =
        yield this.api.shopperBaskets.removePaymentInstrumentFromBasket({
          parameters: {
            basketId: this.basket?.basketId,
            paymentInstrumentId: paymentInstrumentId,
          },
        })
      if (this.basket) {
        this.basket.paymentInstruments = result.paymentInstruments as PaymentInstrument[]
      }
      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *setPromoCode(promoCode: string) {

    try {

      if (!this.basket?.basketId) {
        throw MissingBasketError
      }

      if (!this.basket?.basketId) {
        throw MissingBasketError
      }

      /* Protects applying of new promocode if we have already one in the basket. Max 1 promocode per basket */
      if (this.appliedPromocode?.statusCode === 'APPLIED') {
        throw new Error(
          'You already applied a promo code. Remove the previous promo code to apply new one.'
        )
      }

      this.setStatus(StatusMap.PENDING)

      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-ApplyCoupon?&coupon=${promoCode}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      )

      const json: { couponError?: string, basket: ShopperBaskets.Basket, incompleteOffers: MissedOffers, error: boolean } = yield res.json()

      if (!res.ok || json?.couponError || !json?.basket) {
        throw new Error('Error applying promo code: ' + promoCode)
      }

      this.getOrCreateBasket(json.basket, json.incompleteOffers)

      if (json?.basket?.couponItems && Array.isArray(json.basket.couponItems)) {
        const [appliedCoupon] = json.basket.couponItems
        if (!appliedCoupon.valid || appliedCoupon.statusCode !== "APPLIED") {
          throw new Error(`Coupon code "${promoCode}" is not valid.`)
        }
      } else {
        throw new Error(
          'Error occurred during applying promo code to basket. No coupon items in the basket response.'
        )
      }
      this.setStatus(StatusMap.IDLE)
      return { error: false, message: null }
    } catch (error) {
      console.error(error)
      this.setStatus(StatusMap.ERROR)
      return { error: true, message: `Coupon code "${promoCode}" is not applied.` }
    }
  }

  *removePromoCode(couponItemId: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    this.setStatus(StatusMap.PENDING)

    try {
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-RemoveCoupon?&couponid=${couponItemId}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      )

      const json: { basket: ShopperBaskets.Basket, incompleteOffers: MissedOffers } = yield res.json()

      if (!res.ok || !json?.basket) {
        throw new Error('Error removing promo code: ' + couponItemId)
      }

      this.getOrCreateBasket(json.basket, json.incompleteOffers)

      this.setStatus(StatusMap.IDLE)
      return { error: false, message: null }
    } catch (error) {
      console.error('Unable to remove promo code', error)
      this.setStatus(StatusMap.ERROR)
      return { error: true, message: `Unable to remove promo code` }
    }
  }

  *setCustomerEmail(email: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    this.setStatus(StatusMap.PENDING)
    try {
      const result: CustomerBasket = yield this.api.shopperBaskets.updateCustomerForBasket({
        body: { email },
        parameters: { basketId: this.basket?.basketId },
      })
      this.setBasket(result)
      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  // DF TODO: type the paymentInstrument parameter
  *setPayPalPaymentMethod(paymentInstrument: any) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    let result: CustomerBasket
    this.setStatus(StatusMap.PENDING)

    const existingPaymentInstrumentId =
      this.basket.paymentInstruments && this.basket.paymentInstruments[0]?.paymentInstrumentId

    try {
      result = yield this.api.shopperBaskets.addPaymentInstrumentToBasket({
        body: paymentInstrument,
        parameters: { basketId: this.basket.basketId },
      })
      if (existingPaymentInstrumentId) {
        result = yield this.api.shopperBaskets.removePaymentInstrumentFromBasket({
          parameters: {
            basketId: this.basket.basketId,
            paymentInstrumentId: existingPaymentInstrumentId,
          },
        })
      }
      // DF TODO: This typecast might be dodgy!
      this.basket.paymentInstruments = (result.paymentInstruments as PaymentInstrument[]) ?? []
      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *setPostCode(postcode: string) {
    this.setPostcodeError(false)
    try{
    const response: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-SetPostalCode`,
      {
        method: 'POST',
        body: JSON.stringify({
          postcode: postcode
        }),
        credentials: 'include'
      }
    )

    const {basket, c_customerGroups, regionalIndexConfig, error, rapidDelivery} = yield response.json()

    if (error) {
      throw new Error
    }

    if (basket) {
      yield this.getOrCreateBasket(basket)
    }
    if (c_customerGroups) {
      this.rootStore.customerStore.setCustomerGroups(c_customerGroups)
    }
    if (regionalIndexConfig) {
      this.rootStore.globalStore.setRegionalIndexConfig(regionalIndexConfig)
    } else {
      // If no regional config is returned, set empty config and still trigger algolia index setting
      this.rootStore.globalStore.setRegionalIndexConfig({} as RegionalIndexConfig)
      LocalStorage.available && LocalStorage.remove(LOCALSTORAGE_KEYS.REGIONAL_INDEXES_CONFIG)
    }
    this.rootStore.globalStore.setDataLoadedForAccuratePricingAndAvailabilty()

    this.rootStore.rapidDeliveryStore.setRapidDeliveryAvailability(rapidDelivery.isAvailable)
    this.rootStore.rapidDeliveryStore.setRapidDeliveryShippingCost(rapidDelivery.shippingCost ?? null)
    return postcode
  }

  catch(error) {
    console.error("Postcode Checker Service unavailable")
    this.status = StatusMap.ERROR
    this.setPostcodeError(true)
  }
  }

  *clearPostCode() {
    this.setPostcodeError(false)
    try {
      const response: { success: boolean, basket: CustomerBasket, c_customerGroups?: string[] } = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-ClearPostalCode`,
        {
          method: 'POST',
          credentials: 'include',
        }
      ).then((res) => res.json())

      if (!response.success) {
        throw new Error()
      }

      // Clear the postcode on current basket so setBasket preservation doesn't re-add it
      if (this.basket) {
        this.basket.c_deliveryPostalCode = undefined
      }
      yield this.getOrCreateBasket(response.basket)
      this.rootStore.globalStore.setDataLoadedForAccuratePricingAndAvailabilty()
      // Only clear the regional config if there is no delivery postcode on the basket
      if (!this.basket?.c_deliveryPostalCode) {
        this.rootStore.globalStore.setRegionalIndexConfig({} as RegionalIndexConfig)
      }
      if (response.c_customerGroups) {
        this.rootStore.customerStore.setCustomerGroups(response.c_customerGroups)
      }
      LocalStorage.remove(LOCALSTORAGE_KEYS.CUSTOMER_POSTALCODE)
      this.setNearestStore(null)
    } catch (error) {
      console.error('Error clearing postal code', error)
      this.setPostcodeError(true)
    }
  }

  /**
   * Geocode the delivery postcode and assign the nearest store.
   * @param postcode string postcode to use for search
   * @returns nothing
   */
  async assignNearestStore(postcode: string) {
    try {
      console.log(`Attempting to find nearest store to ${postcode} using google`)

      if (!postcode) {
        this.setNearestStore(null)
        return
      }

      const cachedStore = sessionStorage.getItem('nearestStore')
      const cachedPostal = sessionStorage.getItem('nearestStorePostal')

      if (cachedStore && cachedPostal === postcode) {
        this.setNearestStore(JSON.parse(cachedStore))
        console.log(`Nearest store to ${postcode} set from cache`)
        return
      }

      const coords = await flowResult(this.rootStore.storeDetailsStore.getAddressCoordinates(postcode))

      if (coords) {
        const nearestStore = await flowResult(this.rootStore.storeDetailsStore.findNearestStoreByCoords(coords))
        if (nearestStore) {
          sessionStorage.setItem('nearestStore', JSON.stringify(nearestStore))
          sessionStorage.setItem('nearestStorePostal', postcode)
        }
        this.setNearestStore(nearestStore)
      }
    } catch (e) {
      console.error(`Unable to assign a nearest store for postcode ${postcode}. using google`, e)
      this.setNearestStore(null)
    }
  }

  *getDeliverySlots() {
    this.setDeliverySlotStatus(StatusMap.PENDING)
    try {
      this.setDeliverySlotPostalCode(this.deliveryPostalCode)

      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-GetDeliverySlotsJSON`,
        {credentials: 'include'}
      )
      const json: { slots?: DeliverySlot, error?: string } = yield res.json()

      if (json?.slots) {
        this.setDeliverySlots(json.slots)
      }

      this.setDeliverySlotStatus(StatusMap.DONE)

      // If there is an error, we should set the status to error
      if (json?.error) {
        this.setStatus(StatusMap.ERROR)
        throw new Error('Error fetching delivery slots')
      }

    } catch (error) {
      this.setDeliverySlotStatus(StatusMap.ERROR)
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *getNextAvailableSlot() {
    try {
      this.setDeliverySlotPostalCode(this.deliveryPostalCode)
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-GetNextAvailableDelivery?sameDay=true`,
        { credentials: 'include' }
      )
      const json: { slot?: TimeSlot } = yield res.json()
      if (json?.slot) {
        this.setNextAvailableSlot(json.slot)
      }

    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *reserveDeliverySlot(slotId: string) {
    try {
      const formData = new FormData()
      formData.append('shopping', 'true')
      formData.append('slotId', slotId)
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-ReserveSlot?slotId=${slotId}`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData,
        }
      )

      if (!res.ok) {
        const result: ReserveSlotResponse = {
          result: { code: '400' }
        }
        return result
      }

      const json: ReserveSlotResponse = yield res.json()

      this.setStatus(StatusMap.IDLE)
      const code = json?.result?.code
      if (code && code !== 'success') {
        // Handle the non-success code
        const result: ReserveSlotResponse = {
          result: { code }
        };
        return result
      }
      // Updates the products in the basket
      if (code && code === 'success') {
        if (json.basket) {
          this.getOrCreateBasket(json.basket, json.incompleteOffers)
        }
        if (json.c_customerGroups) {
          this.rootStore.customerStore.setCustomerGroups(json.c_customerGroups)
        }
        this.rootStore.globalStore.setSelectedRegion()
      }

      return json
    } catch (error) {
      const result: ReserveSlotResponse = {
        result: { code: '300' }
      }
      this.setStatus(StatusMap.ERROR)
      console.error(error)
      return result
    }
  }

  // Fetch the Before you go Pages
  *getBeforeYouGoPages(forceUpdate: boolean) {
    try {
      if (!this.beforeYouGoPages || forceUpdate) {
        const res: Response = yield fetch(
          `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/BeforeYouGo-AllPages`,
          { credentials: 'include' }
        )
        const json: { pages?: BeforeYouGoPages } = yield res.json()
        if (json?.pages && Array.isArray(json?.pages)) {
          this.setBeforeYouGoPages(json.pages)
        }
      }
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *getMissedOffers(incompleteOffers?: MissedOffers) {
    if (incompleteOffers) {
      this.setMissedOffers(incompleteOffers)
      return
    }
    try {
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-GetIncompleteOffers`,
        { credentials: 'include' }
      )
      const json: MissedOffers = yield res.json()
      if (json) {
        this.setMissedOffers(json)
      }
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  /**
   * Handles submission of merge basket modal
   */
  async initMergeBasket(useStored: boolean, isSubmit: boolean) {
    let result;
    switch (this.basketMergeType) {
      case BasketMergeTypes.Edit:
        result = await flowResult(this.rootStore.orderStore.initOrderEdit(isSubmit))
        break;
      case BasketMergeTypes.Reorder:
        result = await flowResult(this.rootStore.orderStore.initReorder(isSubmit))
        break;
      default:
        result = await this.mergeBaskets(useStored)
        break;
    }

    this.setInitMergingUI(false)
    this.setBasketMergeData('')
    this.setBasketMergeType(null)

    return result
  }

  // This action is using SCAPI Shopper Baskets endpoint.
  *mergeBaskets(useStored: boolean) {
    try {
      this.setStatus(StatusMap.PENDING)

      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-BasketMerge?usestored=${useStored}`,
        {
          method: 'POST',
          credentials: 'include',
          body: this.basketMergeData,
        }
      )

      if (!res.ok) {
        throw new Error('Error merging basket')
      }

      const json: { success: boolean, basket: ShopperBaskets.Basket } = yield res.json()

      if (json.success && json.basket) {
        this.getOrCreateBasket(json.basket)
      }

      this.setStatus(StatusMap.IDLE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
    }
  }

  buildThresholdItem(promoThreshold: BasketPromotionsThreshold) {
    if (!promoThreshold.ID || !promoThreshold.enabled) {
      return null
    }

    const newThresholdItem = {
      ID: promoThreshold.ID,
      enabled: promoThreshold.enabled,
      thresholdValue: promoThreshold.thresholdValue,
      thresholdSource: promoThreshold.thresholdSource,
      progressText: promoThreshold.progressText,
      completedText: promoThreshold.completedText,
      popoverTitle: promoThreshold.popoverTitle,
      popoverText: promoThreshold.popoverText,
      popoverLink: promoThreshold.popoverLink,
      popoverLinkText: promoThreshold.popoverLinkText,
      showOnComplete: promoThreshold.showOnComplete,
      svg: promoThreshold.svg,
      remaining: 0,
      complete: false,
      progress: 0
    }

    /*
      For the specifically defined thresholds of minimum and free delivery any threshold value will be overridden to ensure they are in line with site
      and predefined keywords will be substituted with dynamic values
    */

    const deliveryCharge = this.basketDeliveryCharge.toFixed(2).replace(/\.00/, '')

    if (newThresholdItem.thresholdSource) {
      if (newThresholdItem.thresholdSource === 'minDelivery' || newThresholdItem.thresholdSource === 'freeDelivery') {
        const freeDeliveryThreshold = this.basketFreeDeliveryThreshold || 0;
        if (newThresholdItem.thresholdSource === 'minDelivery') {
          newThresholdItem.thresholdValue = newThresholdItem.thresholdValue
        } else if (newThresholdItem.thresholdSource === 'freeDelivery') {
          if (!freeDeliveryThreshold) {
            return null
          }
          if (newThresholdItem.popoverText) {
            newThresholdItem.popoverText = newThresholdItem.popoverText.replace('{freeDeliveryThreshold}', freeDeliveryThreshold.toFixed(2).replace(/\.00/, ''))
            newThresholdItem.thresholdValue = freeDeliveryThreshold
          }
        }
        if (newThresholdItem.popoverText) {
          if (deliveryCharge != 0) {
            newThresholdItem.popoverText = newThresholdItem.popoverText.replace('{charge}', deliveryCharge)
          }
          else {
            newThresholdItem.popoverText = newThresholdItem.popoverText.replace('£{charge} delivery or', '')
          }
        }
      } else if (newThresholdItem.thresholdSource === 'smallBasketChargeAndDeliveryCharge' || newThresholdItem.thresholdSource === 'smallBasketChargeAndFreeDelivery') {
        const smallBasketChargeThreshold = this.basketSmallBasketChargeThreshold
        const smallBasketCharge = this.basketSmallBasketCharge
        if (!smallBasketChargeThreshold || !smallBasketCharge) {
          return null
        }

        if (newThresholdItem.thresholdSource === 'smallBasketChargeAndDeliveryCharge') {
          if (deliveryCharge == 0) {
            return null
          }
          if (newThresholdItem.progressText) {
            newThresholdItem.progressText = newThresholdItem.progressText.replace('{charge}', `£${deliveryCharge}`)
          }
          if (newThresholdItem.completedText) {
            newThresholdItem.completedText = newThresholdItem.completedText.replace('{charge}', deliveryCharge)
          }
        }

        if (newThresholdItem.thresholdSource === 'smallBasketChargeAndFreeDelivery') {
          if (deliveryCharge > 0) {
            return null
          }
          if (newThresholdItem.progressText) {
            newThresholdItem.progressText = newThresholdItem.progressText.replace('{smallBasketChargeThreshold}', `£${smallBasketChargeThreshold.toFixed(2)}`)
          }
        }
        if (newThresholdItem.popoverText) {
          newThresholdItem.popoverText = newThresholdItem.popoverText.replace('{smallBasketChargeThreshold}', smallBasketChargeThreshold.toFixed(2).replace(/\.00/, ''))
          newThresholdItem.popoverText = newThresholdItem.popoverText.replace('{smallBasketCharge}', smallBasketCharge.toFixed(2).replace(/\.00/, ''))
        }
        newThresholdItem.thresholdValue = smallBasketChargeThreshold;

      }
    }

    newThresholdItem.progress = Math.min((100 / newThresholdItem.thresholdValue) * (this.basket?.productTotal || 0), 100)
    newThresholdItem.complete = (newThresholdItem.progress === 100)

    if (newThresholdItem.complete && !newThresholdItem.showOnComplete) {
      return null
    }
    newThresholdItem.remaining = (newThresholdItem.thresholdValue - (this.basket?.productTotal || 0))
    if (newThresholdItem.progressText) {
      const price = newThresholdItem.remaining < 1 ? (newThresholdItem.remaining * 100).toFixed(0).toString() + 'p' : `£${newThresholdItem.remaining.toFixed(2)}`
      newThresholdItem.progressText = newThresholdItem.progressText.replace('{amt}', price)
    }
    return newThresholdItem
  }

  *transformPromotionalThresholds() {
    const promoThresholds = []

    if (this.promotionalThresholds?.length) {
      for (const thresholdItem of this.promotionalThresholds) {
        const thresholdObject = this.buildThresholdItem(thresholdItem)
        if (thresholdObject != null) {
          promoThresholds.push(thresholdObject)
        }
      }

      promoThresholds.sort(function (a, b) {
        return a.thresholdValue > b.thresholdValue ? 1 : -1
      });

      //ensure only up to first unfulfilled threshold returned to view
      let lastRelaventMessageIndex = 0;
      for (let i = 0; i < promoThresholds.length; i++) {
        lastRelaventMessageIndex = i
        if (!promoThresholds[i].complete) {
          break
        }
      }

      this.setFilteredPromotionalThresholds(promoThresholds.slice(0, lastRelaventMessageIndex + 1))

    }
  }

  *getBasketPromotionalThreshold(forceUpdate = false) {
    try {
      if (forceUpdate || !this.promotionalThresholds?.length) {
        const res: Response = yield fetch(
          `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-GetPromoThresholds`,
          { credentials: 'include' }
        )
        const json: BasketPromotionsThresholdResponse = yield res.json()
        if (json && json?.promoThresholds?.length) {
          this.setPromotionalThresholds(json.promoThresholds)
        }
      }
      if (this.promotionalThresholds?.length) {
        yield this.transformPromotionalThresholds()
      }
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      throw error
    }
  }

  *handlePostcodeDeliveryStatus(postcode: string) {
    try {
      this.setProcessingAppliedPostCode(true)
      const response: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-GetDeliveryStatus?postcode=${encodeURIComponent(
          postcode
        )}`,
        {
          credentials: 'include',
        }
      )
      if (!response.ok) throw new Error(`Error during getting Delivery status for ${postcode}`)

      const json: {
        data: any,
        error?: object
      } = yield response.json()

      if (typeof json?.data?.code !== 'boolean') {
        return false
      }

      if (json?.error) {
        this.setStatus(StatusMap.ERROR)
        return json.error
      }

      // if postcode is not deliverable, set nearest store
      if (!json?.data?.code) {
        if (json?.data?.nearestStore) {
          // Set store from response
          console.log(`Setting nearest store to ${postcode} from delivery status`)
          this.setNearestStore(json.data.nearestStore)
        }
        else {
          // Fallback to google maps geocode if we couldn't find a matching store
          this.assignNearestStore(postcode)
        }
      }

      return json.data.code
    } catch (error) {
      console.error(error)
      return false
    } finally {
      this.setProcessingAppliedPostCode(false)
    }
  }

}

// @TODO: Investigate if we need this class and transform cart items in transformBasket mapper
export class ProductItem extends Wrapper<TProductItem>() {
  // basketStore: BasketStore
  constructor(basketStore: BasketStore, productItem: TProductItem) {
    super(productItem)
  }
}

export const transformBasket = (basketStore: BasketStore, basket: CustomerBasket): StoreBasket => {
  const transformedBasket = { ...basket }
  if (!transformedBasket.productItems) {
    transformedBasket.productItems = []
    return transformedBasket as StoreBasket
  }
  transformedBasket.productItems =
    transformedBasket.productItems.map((item) => new ProductItem(basketStore, item)) || []

  return transformedBasket as StoreBasket
}
